import { createRoute, getRouteApi } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { menuLayoutRoute } from 'root/MenuLayout';
import RequireSingleCountry from 'root/RequireSingleCountry';

import { SelectionRecommendationsView } from './SelectionRecommendationsView';

const listingSortByOptions = [
  'NET_REVENUE_SHARE_DESC',
  'NET_REVENUE_SHARE_ASC',
  'CURRENT_SKU_COUNT_DESC',
  'CURRENT_SKU_COUNT_ASC',
  'GAP_GLOBAL_AVERAGE_DESC',
  'GAP_GLOBAL_AVERAGE_ASC',
  'GAP_BEST_IN_CLASS_DESC',
  'GAP_BEST_IN_CLASS_ASC',
  'EXPECTED_BUSINESS_IMPACT_DESC',
  'EXPECTED_BUSINESS_IMPACT_ASC',
] as const;

const delistingSortByOptions = [
  'EXPECTED_BUSINESS_IMPACT_ASC',
  'EXPECTED_BUSINESS_IMPACT_DESC',
  'SOLD_UNITS_ASC',
  'SOLD_UNITS_DESC',
  'TOTAL_REVENUE_ASC',
  'TOTAL_REVENUE_DESC',
  'PURE_PROFIT_MARGIN_ASC',
  'PURE_PROFIT_MARGIN_DESC',
  'PURE_PROFIT_MARGIN_DELTA_BPS_ASC',
  'PURE_PROFIT_MARGIN_DELTA_BPS_DESC',
] as const;

const promotionSortByOptions = [
  'EXPECTED_BUSINESS_IMPACT_ASC',
  'EXPECTED_BUSINESS_IMPACT_DESC',
  'SOLD_UNITS_ASC',
  'SOLD_UNITS_DESC',
  'TOTAL_REVENUE_ASC',
  'TOTAL_REVENUE_DESC',
  'PURE_PROFIT_MARGIN_ASC',
  'PURE_PROFIT_MARGIN_DESC',
  'PURE_PROFIT_MARGIN_DELTA_BPS_ASC',
  'PURE_PROFIT_MARGIN_DELTA_BPS_DESC',
] as const;

const SelectionRecommendationsActiveTabEnum = z.enum([
  'LISTING_RECOMMENDATIONS',
  'DELISTING_RECOMMENDATIONS',
  'PROMOTION_RECOMMENDATIONS',
]);

export type SelectionRecommendationsActiveTabEnumType = z.infer<
  typeof SelectionRecommendationsActiveTabEnum
>;

const selectionRecommendationsSearchParamsSchema = z.object({
  timePeriod: z.enum(['LAST_WEEK', 'LAST_MONTH']).optional(),
  listingSortBy: z.enum(listingSortByOptions).optional(),
  activeTab: SelectionRecommendationsActiveTabEnum.optional(),
  delistingsSortBy: z.enum(delistingSortByOptions).optional(),
  promotionsSortBy: z.enum(promotionSortByOptions).optional(),
  venueIds: z.array(z.string()).optional(),
  brandIds: z.array(z.string()).optional(),
  delistingReasons: z
    .array(z.enum(['NO_SALES', 'LOW_PPM', 'HIGH_SHRINKAGE', 'LOW_BASKET_PENETRATION']))
    .optional(),
  listingReasons: z.array(z.enum(['UNDERDEVELOPED', 'MISSING'])).optional(),
  vendorIds: z.array(z.string()).optional(),
});

const selectionRecommendationsMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'selection-recommendations',
  component: RequireSingleCountry,
});

export type SelectionRecommendationsSearchRouteSchemaType = z.infer<
  typeof selectionRecommendationsSearchParamsSchema
>;

export const selectionRecommendationsRouteApi = getRouteApi(
  '/auth/merchant/menuLayout/selection-recommendations/',
);

const selectionRecommendationView = createRoute({
  getParentRoute: () => selectionRecommendationsMainRoute,
  path: '/',
  component: SelectionRecommendationsView,
  validateSearch: selectionRecommendationsSearchParamsSchema.parse,
});

export const selectionRecommendationsRouteTree = selectionRecommendationsMainRoute.addChildren([
  selectionRecommendationView,
]);
